import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, Drawer, IconButton } from '@mui/material';
import useResponsive from '../../hooks/responsive.hook';
import Logo from '../common/logo.component';
import Scrollbar from '../common/scrollbar.component';
import NavItems from './sidebar/nav-items.component';
import ROUTE_PATH from '../../config/routes.config';
import Iconify from '../common/iconify.component';

const NAV_WIDTH = 280;
const NAV_COLLAPSE_WIDTH = 88;

const Sidebar = ({
  openNav,
  onOpenNav,
  onCloseNav,
  categoriesTab,
  isCollapse,
  setIsCollapse,
}) => {
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          height: { xs: 64, lg: 92 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <NavLink
          to={ROUTE_PATH.DASHBOARD}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            visibility: isCollapse ? 'hidden' : 'visible',
            width: isCollapse ? 0 : 'auto',
            opacity: isCollapse ? 0 : 1,
            transition: 'all 0.3s ease',
          }}
        >
          <Logo sx={{ width: 130 }} />
        </NavLink>
        <Box
          onClick={() => setIsCollapse(!isCollapse)}
          sx={{
            cursor: 'pointer',
            color: '#637381',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.3s ease',
            position: isCollapse ? 'absolute' : 'relative',
            left: isCollapse ? '50%' : 'auto',
            transform: isCollapse ? 'translateX(-50%) rotate(180deg)' : 'none',
          }}
        >
          <Iconify icon="eva:arrow-ios-back-outline" width={18} height={18} />
        </Box>
      </Box>

      <NavItems categoriesTab={categoriesTab} isCollapse={isCollapse} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        onClick={onOpenNav}
        sx={{
          display: { lg: 'none' },
          height: 'fit-content',
          position: 'absolute',
          top: 18,
          left: 25,
          mr: 1,
          color: 'text.primary',
          zIndex: 1111,
        }}
      >
        <Iconify icon="eva:menu-2-fill" />
      </IconButton>

      <Box
        component="nav"
        sx={{
          flexShrink: { lg: 0 },
          width: { lg: isCollapse ? NAV_COLLAPSE_WIDTH : NAV_WIDTH },
          transition: 'width 0.3s ease',
        }}
      >
        {isDesktop ? (
          <Drawer
            open
            variant="permanent"
            PaperProps={{
              sx: {
                width: isCollapse ? NAV_COLLAPSE_WIDTH : NAV_WIDTH,
                bgcolor: '#FFFFFF',
                borderRightStyle: 'dashed',
                transition: 'width 0.3s ease',
              },
            }}
          >
            {renderContent}
          </Drawer>
        ) : (
          <Drawer
            open={openNav}
            onClose={onCloseNav}
            ModalProps={{
              keepMounted: true,
            }}
            PaperProps={{
              sx: {
                width: isCollapse ? NAV_COLLAPSE_WIDTH : NAV_WIDTH,
                bgcolor: '#FFFFFF',
                transition: 'width 0.3s ease',
              },
            }}
          >
            {renderContent}
          </Drawer>
        )}
      </Box>
    </>
  );
};

Sidebar.propTypes = {
  openNav: PropTypes.bool.isRequired,
  onOpenNav: PropTypes.func.isRequired,
  onCloseNav: PropTypes.func.isRequired,
  categoriesTab: PropTypes.array.isRequired,
  isCollapse: PropTypes.bool,
  setIsCollapse: PropTypes.func,
};

Sidebar.defaultProps = {
  isCollapse: false,
  setIsCollapse: () => {},
};

export default Sidebar;
